<template>
  <div id="app">
    <h1>WordMerge</h1>
    <h4 class="clue-container">Clue: {{ clue }}</h4>
    <br>
    <div class="resource-container">
      <div class="resource" v-for="(isSelected, resource, index) in resources" :key="index" :class="[isSelected ? 'selected' : '', resource]" @click="toggleSelection(resource)" ref="resourceElements">
        <p>{{ resource }}</p>
      </div>
      <div v-for="i in Math.max(0, 9 - Object.keys(resources).length) " :key="`empty-${i}`" class="resource empty">
        <p>_</p>
      </div>
    </div>

    <div ref="scoreText" class="score-text">
    </div>

    <div class="blocks-container">
      <div v-for="(index, key) in combinedBlocks" :key="key" :class="[index.startsWith('yellow') ? 'yellow-block' : (index.startsWith('red') ? 'red-block' : 'green-block')]"></div>
    </div>

    <div>
      <button v-if="!gameEnded" class="combine-button" @click="submitCombination" :disabled="selectedResources.length !== 2">Submit</button>
      <button v-if="gameEnded" class="combine-button" @click="openCompletionModal">Show Score</button>
      <br>
      <br>
      <button class="instruction-button" @click="openInstructionModal">Instructions</button>
    </div>

    <div class="modal" v-if="showInstructionModal" @click="closeModalOnClickOutside">
      <div class="modal-content">
        <span class="close-modal" @click="closeInstructionModal">&times;</span>
        <div class="instruction-content">
          <h2>How to Play</h2>
          <ul>
            <li class="instruction-content-header"><b>Goal</b></li>
            <li>• Combine two words to reveal a new word</li>
            <li>• You need to iteratively combine 3 times to get to a final word that solves the puzzle</li>
            <li>• The clue has subtle hints for all 3 combines</li>
          </ul>
          <ul>
            <li class="instruction-content-header"><b>Types of Combines</b></li>
            <li>Literal → cup + board = cupboard</li>
            <li>Semantic → woman + king = queen</li>
            <!-- <li>Association → stone + tough = hard</li> -->
            <li>Phonetic → may + eight = mate</li>
          </ul>
          <ul>
            <li class="instruction-content-header"><b>Rules</b></li>
            <li>• You may reuse words</li>
            <li>• You have 7 tries to get it right</li>
            <li>• You might not use all the words</li>
            <li>• Any combines can work. Does not have to be one of each</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="modal" v-if="showCompletionModal" @click="closeModalOnClickOutside">
      <div class="modal-content">
        <span class="close-modal" @click="closeCompletionModal">&times;</span>
        <div v-if="isWinner">
          <h1>You Win!</h1>
        </div>
        <div v-else>
          <h1>Game Over</h1>
        </div>
        
        <h3>Answer: {{ goal }}</h3>
        <p v-html="solutionPath"></p>
        
        <h3>Your Score</h3>
          <div class="blocks-container score-container">
            <div v-for="(index, key) in combinedBlocks" :key="key" :class="[index.startsWith('yellow') ? 'yellow-block' : (index.startsWith('red') ? 'red-block' : 'green-block')]"></div>
          </div>
          <br>
        <button v-if="copied" class="share-button">
          Copied!
        </button>
        <button v-else class="share-button" @click="copyBlocks">
          Copy & Share
        </button>
      </div>
    </div>

    <footer style="font-size: smaller; margin-top: 20px;">
        <p>{{ currentDate }} - No. {{ puzzleNumber }}</p>
        <p>Released daily at midnight ET</p>
    </footer>
    Sponsored by 
    <a href="https://www.edvision.ai" style="color: #1e90ff; text-decoration: none; font-weight: bold;">
        EdVision: degree audit software
    </a>
  </div>
</template>
<script>
export default {
  name: 'App',
  data() {
    return {
      clue: "fly on water",
      resources: {
        float: false,
        rain: false,
        see: false,
        machine: false,
        air: false,
        tail: false,
      },
      validCombinations: [
        { input: ['float', 'air'], output: 'fly' },
        { input: ['fly', 'machine'], output: 'plane' },
        { input: ['see', 'plane'], output: 'seaplane' },
      ],
      goal: 'seaplane',
      puzzleNumber: 52,
      scores: [],
      showInstructionModal: true,
      showCompletionModal: false,
      gameEnded: false,
      isWinner: false,
      attempts: [],
      copied: false,
    };
  },

  methods: {
    toggleSelection(resource) {
      if (!this.gameEnded) {
        if (this.resources[resource] || this.selectedResources.length < 2) {
          this.resources[resource] = !this.resources[resource];
          const isSelected = this.resources[resource];
          const selectedResource = this.$refs.resourceElements.find(element => element.classList.contains(resource));
          selectedResource.style.backgroundColor = isSelected ? 'rgb(89, 96, 98)' : '';
          selectedResource.style.color = isSelected ? 'white' : 'black';
        }
      }
    },
    submitCombination() {
      const rotationDelay = 200;
      const selectedResources = this.selectedResources;
      if (selectedResources.length === 2) {
        if (this.attempts.includes(this.sortedSelectedResources)) {
          // If the combination has been attempted before, trigger vibration effect and display message
          this.selectedResources.forEach(resource => {
            const selectedResource = this.$refs.resourceElements.find(element => element.classList.contains(resource));
            if (selectedResource) {
              this.vibrate(selectedResource);
            }
          });
          this.displayScoreText('You already tried this combination.');
          setTimeout(() => {
            this.clearVibration(selectedResources);
            this.clearScoreText();
          }, 2000);
        } else {
          this.attempts.push(this.sortedSelectedResources);
          const { combination: validCombination, selectedWords: validWords, onboardingMessage: userMsg } = this.findCombination(selectedResources);

          // Apply rotation animation to selected resource buttons one by one
          var animationCount = 0
          this.selectedResources.forEach((resource, index) => {
            const selectedResource = this.$refs.resourceElements.find(element => element.classList.contains(resource));
            if (selectedResource) {
              animationCount += 1;
              setTimeout(() => {
                selectedResource.style.animation = 'rotateAnimation 1s ease forwards';

                setTimeout(() => {
                  if (validCombination) {
                    selectedResource.style.backgroundColor = 'green';
                    selectedResource.style.color = 'white';
                  } else if (validWords && validWords.includes(resource)) {
                    selectedResource.style.backgroundColor = '#D4AF37';  // yellow
                    selectedResource.style.color = 'white';
                  } else {
                    selectedResource.style.backgroundColor = '#990000';  // red
                    selectedResource.style.color = 'white';
                  }

                  // Clear animation after it completes
                  setTimeout(() => {
                    selectedResource.style.animation = '';
                    selectedResource.style.backgroundColor = '';
                    selectedResource.style.color = 'black';
                  }, 500);

                }, rotationDelay);

              }, index * rotationDelay);
            }
          });

          setTimeout(() => {
            var output = null;
            if (validCombination) {
              output = this.executeCombination(validCombination);
              this.scores.push('green');
            } else if (validWords) {
              this.scores.push('yellow');
              this.checkAndDisplayOnboardingMessage(userMsg);
            } else {
              this.scores.push('red');
              this.checkAndDisplayOnboardingMessage(userMsg);
            }

            if (output === this.goal) {
              this.isWinner = true;
              this.showCompletionModal = true;
              this.gameEnded = true;
            } else if (this.scores.filter(item => item !== 'green').length >= 7) {
              this.isWinner = false;
              this.showCompletionModal = true;
              this.gameEnded = true;
            }
          }, animationCount * rotationDelay + 600);
        }
      }
      this.clearSelection(selectedResources);
    },
    findCombination(selectedResources) {
      /*
        Returns a valid combination as an object { combination, selectedWords, onboardingMessage }
      */
      for (const combination of this.validCombinations) {
        if (selectedResources.includes(combination.input[0]) && selectedResources.includes(combination.input[1])) {
          return { combination, selectedWords: selectedResources, onboardingMessage: null };
        }
      }

      // Check if any of the selected words are part of a valid combination
      const availableCombos = [];
      for (const combo of this.validCombinations) {
        const [input1, input2] = combo.input;
        if (input1 in this.resources && input2 in this.resources && !(combo.output in this.resources)) {
          availableCombos.push(...combo.input);
        }
      }

      const selectedWords = selectedResources.filter(word => availableCombos.includes(word));
      if (selectedWords.length === 2) {
        return { combination: null, selectedWords, onboardingMessage: "Incorrect pair, but both words are part of a valid pair!" };
      } else if (selectedWords.length === 1) {
        return { combination: null, selectedWords, onboardingMessage: "1 selected word is correct!" };
      } else {
        return { combination: null, selectedWords: null, onboardingMessage: null };
      }
    },
    checkAndDisplayOnboardingMessage(msg) {
      if (
        this.scores.filter(score => score === 'green').length >= 1 &&
        this.scores.filter(score => score === 'red' || score === 'yellow').length >= 1
      ) {
        if (this.scores.at(-1) === 'yellow') {
          this.displayScoreText(msg + " Also, you may use words created from previous merges");
        } else {
          this.displayScoreText("You may need to use words created from previous merges");
        }
        setTimeout(() => {
          this.clearScoreText();
        }, 4000);
      } else if (this.scores.at(-1) === 'yellow') {
        this.displayScoreText(msg);
        setTimeout(() => {
          this.clearScoreText();
        }, 2500);
      } else if (this.scores.at(-1) === 'red') {
        if (msg) {
          this.displayScoreText(msg + " Use the clue for a hint!");
        } else {
          this.displayScoreText("Use the clue for a hint!");
        }
        setTimeout(() => {
          this.clearScoreText();
        }, 2500);
      }
    },
    executeCombination(combination) {
      const outputElement = combination.output;
      this.resources[outputElement] = false;

      // // Iterate over each resource element and clear its animation
      // this.$refs.resourceElements.forEach(element => {
      //   element.style.animation = '';
      //   element.style.backgroundColor = '';
      //   element.style.color = 'black';
      // });

      return outputElement;
    },
    clearSelection(selectedResources) {
      const [elem1, elem2] = selectedResources;
      // Remove the selected elements from resources
      this.resources[elem1] = false;
      this.resources[elem2] = false;

      // Reset the background color and text color of the cleared resource tile
      const clearedResource1 = this.$refs.resourceElements.find(element => element.classList.contains(elem1));
      const clearedResource2 = this.$refs.resourceElements.find(element => element.classList.contains(elem2));
      if (clearedResource1) {
        clearedResource1.style.backgroundColor = '';
        clearedResource1.style.color = 'black';
      }
      if (clearedResource2) {
        clearedResource2.style.backgroundColor = '';
        clearedResource2.style.color = 'black';
      }
    },
    displayScoreText(text) {
      this.$refs.scoreText.textContent = text;
    },
    clearScoreText() {
      this.$refs.scoreText.textContent = '';
    },
    openInstructionModal() {
      this.showInstructionModal = true;
    },
    closeInstructionModal() {
      this.showInstructionModal = false;
    },
    openCompletionModal() {
      this.showCompletionModal = true;
    },
    closeCompletionModal() {
      this.showCompletionModal = false;
    },
    closeModalOnClickOutside(event) {
      if (event.target.classList.contains('modal')) {
        this.closeInstructionModal();
        this.closeCompletionModal();
      }
    },
    copyBlocks() {
      const blocks = document.querySelectorAll('.score-container .red-block, .score-container .green-block, .score-container .yellow-block');
      const blockData = Array.from(blocks).map(block => {
        return block.classList.contains('yellow-block') ? '🟨' : (block.classList.contains('red-block') ? '🟥' : '🟩');
      }).join('');
      const msg = 'WordMerge #' + this.puzzleNumber + '\n' + blockData;
      navigator.clipboard.writeText(msg).then(() => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      }).catch(err => {
        console.error('Error copying to clipboard:', err);
      });
    },
    shareBlocks() {  // Deprecated.
      const blocks = document.querySelectorAll('.score-container .red-block, .score-container .green-block, .score-container .yellow-block');
      const blockData = Array.from(blocks).map(block => {
        return block.classList.contains('yellow-block') ? '🟨' : (block.classList.contains('red-block') ? '🟥' : '🟩');
      }).join(''); // Insert line breaks between emojis

      const msg = 'WordMerge #' + this.puzzleNumber + '\n' + blockData;
      const shareLink = 'https://wordmerge.netlify.app';
      // Check if the environment supports sharing via navigator.share
      if (navigator.share) {
        navigator.clipboard.writeText(msg).then(() => {
          navigator.share({
            title: "",
            text: msg,
            url: ""
          }).then(() => {
            console.log('Shared successfully');
          }).catch((error) => {
            console.error('Error sharing:', error);
          });
        });
      } else {
        // Fallback for environments that do not support navigator.share
        // You can provide a shareable link for manual sharing
        // For example, open a new tab with the shareable link
        window.open(shareLink);
      }
    },
    vibrate(element) {
      // Apply vibration effect by shifting the element's position left and right
      let shift = 10;
      const duration = 100;
      const interval = 10;
      const originalPosition = parseFloat(getComputedStyle(element).marginLeft);
      const vibrations = Math.floor(duration / interval);
      let count = 0;
      const vibrateInterval = setInterval(() => {
        element.style.marginLeft = (originalPosition + (shift * Math.sin(count * Math.PI / vibrations))) + 'px';
        count++;
        if (count >= vibrations) {
          clearInterval(vibrateInterval);
        }
      }, interval);
    },
    clearVibration(selectedResources) {
      // Reset the position of selected resources after vibration effect
      selectedResources.forEach(resource => {
        const selectedResource = this.$refs.resourceElements.find(element => element.classList.contains(resource));
        if (selectedResource) {
          selectedResource.style.marginLeft = '0';
        }
      });
    },
  },

  computed: {
    selectedResources() {
      return Object.keys(this.resources).filter(resource => this.resources[resource]);
    },
    combinedBlocks() {
      const blocks = [];
      for (let i = 0; i < this.scores.length; i++) {
        blocks.push(this.scores[i] + '-block-' + (i + 1));
      }
      return blocks;
    },
    currentDate() {
      const today = new Date();
      const options = { month: 'long', day: 'numeric', year: 'numeric' };
      return today.toLocaleDateString('en-US', options);
    },
    sortedSelectedResources() {
      // Make a copy of the original array to avoid mutating it directly
      const sortedSelected = [...this.selectedResources];
      // Sort the copied array by item name in ascending order
      sortedSelected.sort((a, b) => {
        return a.localeCompare(b);
      });
      return sortedSelected.join(',_');
    },
    solutionPath() {
      const path = [];
      for (let combination of this.validCombinations) {
        let input1 = combination.input[0];
        let input2 = combination.input[1];
        let output = combination.output;
        input1 = `<strong>${input1}</strong>`;
        input2 = `<strong>${input2}</strong>`;
        output = `<strong>${output}</strong>`;
        path.push(`${input1} + ${input2} = ${output}`);
      }
      return path.join('<br>');
    },
    webShareApiSupported() {
      return navigator.share
    },
  },

  mounted() {
    document.addEventListener('click', this.closeModalOnClickOutside);
  },
  unmounted() {
    document.removeEventListener('click', this.closeModalOnClickOutside);
  }
};
</script>

<style>
body {
  font-family: Arial, sans-serif;
  text-align: center;
}

.clue-container {
  color: #1877F2;
  /* Facebook blue */
  border: 2px solid #1877F2;
  /* Add a dashed border outline */
  border-radius: 5px;
  /* Round the corners */
  padding: 10px;
  /* Add some padding for better appearance */
  display: inline-block;
  /* Make sure the border only wraps around the text */
}

.resource p {
  font-size: smaller;
}

.resource-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.resource {
  display: inline-block;
  margin: 10px;
  cursor: pointer;
  width: calc(33% - 30px);
  /* Adjusted width to accommodate 4 resources per row */
  outline: 1px solid black;
  border-radius: 10px;
  animation-duration: 1s;
  /* Duration of the animation */
  animation-fill-mode: forwards;
  /* Keeps the element in the rotated state after the animation ends */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
}

.empty {
  background-color: black;
  opacity: 0.1;
}
  
@keyframes rotateAnimation {
  0% {
    transform: rotateX(0deg);
  }

  100% {
    transform: rotateX(360deg);
  }
}

.selected {
  background-color: rgb(89, 96, 98);
  color: white;
}

ul {
  list-style: none;
  padding: 0;
}

.score-text {
  font-style: italic;
}

.score-text,
.blocks-container {
  display: flex;
  justify-content: center; /* Center the elements horizontally */
  /* flex-direction: column-reverse; */
  /* Stack blocks in reverse order */
  /* align-items: flex-start; */
  margin-top: 20px;
  text-align: center;
}

.red-block::before {
  content: "🟥"
}

.green-block::before {
  content: "🟩"
}

.yellow-block:before {
  content: "🟨";
}

.yellow-block,
.red-block,
.green-block {
  margin-right: 5px;
}

.yellow-block:last-child,
.red-block:last-child,
.green-block:last-child {
  margin-right: 0;
}

.share-button,
.combine-button {
  background-color: black;
  color: white;
  padding: 10px 20px;
  border: none;
  cursor: pointer;
  transition: transform 0.3s ease;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
}

.combine-button {
  margin-top: 20px;
}

.combine-button:hover {
  transform: translateY(-5px);
}
.combine-button:active {
  transform: translateY(0);
}
.combine-button:disabled {
  opacity: 0.5;
  /* Reduce opacity when button is disabled */
}

.instruction-button {
  background-color: black;
  opacity: 0.5;
  color: white;
  padding: 5px 10px;
  border: none;
  cursor: pointer;
  transition: transform 0.3s ease;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
}

.modal {
  /* display: none; */
  position: fixed;
  z-index: 1;
  left: 0;
  top: 0;
  width: 100%;
  height: 120%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.756);
}

.modal-content {
  margin: 5% auto;
  padding: 8px;
  border: 1px solid #888;
  width: 80%;
  background-color: #fff;
  border-radius: 10px;
}

.close-modal {
  color: #aaa;
  float: right;
  font-size: 28px;
  font-weight: bold;
}

.close-modal:hover,
.close-modal:focus {
  color: black;
  text-decoration: none;
  cursor: pointer;
}

.instruction-content {
  max-width: 600px;
  margin: 10px auto;
  padding: 10px;
}

.instruction-content p {
  text-align: left;
  /* margin-bottom: 10px; */
  /* line-height: 1.5; */
}

.instruction-content ul, li {
  text-align: left;
  padding-left: 3px;
  margin-bottom: 1px;
}

.instruction-content-header {
  margin-bottom: 5px;
  padding-left: 0px;
}
</style>
